import { UserAuthWrapper } from 'redux-auth-wrapper';
import lodash from 'lodash';
import {
    redirectWithMessage,
    goBackWithMessage,
    couldNotFindRoute
} from 'common/actions/permissions';
import {
    loadingMeSelector,
    loadingBinderSelector,
    meSelector,
    binderPermissionsSelector,
    filePermissionsSelector,
    binderMenuPermissionsSelector,
} from 'common/permissions/selectors';
import {
    isAuthenticated,
    createBinderPredicate,
    manageStudyPermissionsPredicate,
    createStudyPredicate,
    dataSourcesPredicate,
    canAccessBinderTaskPredicate,
    institutionalAdminPredicate,
    manageInternalUsersPredicate,
    manageExternalUsersPredicate,
    editBinderDetailsPredicate,
    editBinderQueryPredicate,
    timelinePredicate,
    compilePredicate,
    reportPredicate,
} from 'common/permissions/predicates';

import {
    viewStudyExpectationsPredicate,
    manageStudyExpectationsPredicate,
    viewStudyDashboardPredicate,
    viewStudyEventsPredicate,
    manageStudyTasksPredicate,
    userCanAccessStudyDelegation,
} from 'common/permissions/studyPredicates';

import {
    binderValidationFeatureAccess,
    doaFeatureAccess,
    binderEventFeatureAccess,
    currentStudyEventAccess,
    studyFeatureAccess,
    dashboardFeatureAccess,
    manageEventTypeFeatureAccess,
    loadHomePageV4Access,
} from 'common/features/predicates';
import LoadingComponent from 'common/permissions/components/LoadingComponent';
import { homeRouteSelector, binderRouteSelector, defaultStudyFallbackRouteSelector } from 'common/features/selectors';


export const AlwaysRedirectHome = UserAuthWrapper({
    authSelector: () => null,
    predicate: () => false,
    failureRedirectPath: (state) => homeRouteSelector(state),
    redirectAction: couldNotFindRoute
});

export const ForAuthenticatedUsers = UserAuthWrapper({
    authSelector: meSelector,
    authenticatingSelector: loadingMeSelector,
    predicate: isAuthenticated,
    failureRedirectPath: '/',
    redirectAction: redirectWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersWhoCanAccessBinderTask = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: canAccessBinderTaskPredicate,
    failureRedirectPath: (state) => homeRouteSelector(state),
    redirectAction: redirectWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForInstitutionalAdmins = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: institutionalAdminPredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersWhoCreateBinders = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: createBinderPredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersWhoManageStudyPermissions = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: manageStudyPermissionsPredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersWhoCreateStudies = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: createStudyPredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersWhoCanEditBinderDetails = UserAuthWrapper({
    authSelector: binderPermissionsSelector,
    authenticatingSelector: loadingBinderSelector,
    predicate: editBinderDetailsPredicate,
    failureRedirectPath: (state) => homeRouteSelector(state),
    redirectAction: redirectWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersWhoCanEditBinderQuery = UserAuthWrapper({
    authSelector: binderPermissionsSelector,
    authenticatingSelector: loadingBinderSelector,
    predicate: editBinderQueryPredicate,
    failureRedirectPath: (state) => homeRouteSelector(state),
    redirectAction: redirectWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersWithAccessToBinder = UserAuthWrapper({
    authSelector: binderMenuPermissionsSelector,
    authenticatingSelector: loadingBinderSelector,
    predicate: ({ binderPermissions, binder }) => binderPermissions.hasBinderAccess && binder.status !== 'REMOVED',
    failureRedirectPath: (state) => homeRouteSelector(state),
    redirectAction: redirectWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersWithSpecificFilePermissions = UserAuthWrapper({
    authSelector: filePermissionsSelector,
    authenticatingSelector: loadingBinderSelector,
    predicate: ({ location, binderPermissions }) => {
        if (lodash.has(location.query, 'upload') && !binderPermissions.upload) {
            return false;
        }
        if (lodash.get(location.query, 'folder') === 'unfiled' && !binderPermissions.unfiledDocuments) {
            return false;
        }
        return true;
    },
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersThatCanManageInternalUserAccess = UserAuthWrapper({
    authSelector: binderPermissionsSelector,
    authenticatingSelector: loadingBinderSelector,
    predicate: manageInternalUsersPredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersThatCanManageExternalUserAccess = UserAuthWrapper({
    authSelector: binderPermissionsSelector,
    authenticatingSelector: loadingBinderSelector,
    predicate: manageExternalUsersPredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersThatCanCompileBinders = UserAuthWrapper({
    authSelector: binderPermissionsSelector,
    authenticatingSelector: loadingBinderSelector,
    predicate: compilePredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersThatCanSeeBinderTimeline = UserAuthWrapper({
    authSelector: binderPermissionsSelector,
    authenticatingSelector: loadingBinderSelector,
    predicate: timelinePredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersThatCanViewDataSources = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: dataSourcesPredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

export const ForUsersThatCanViewReport = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: reportPredicate,
    redirectAction: goBackWithMessage,
    LoadingComponent: LoadingComponent
});

/**
 * Feature Access Auth Wrappers
 */
export const StudyDoaFeatureAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingBinderSelector,
    predicate: userCanAccessStudyDelegation,
    failureRedirectPath: (state) => homeRouteSelector(state)
});

export const ForUsersAtInstitutionsWithDoA = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingBinderSelector,
    predicate: doaFeatureAccess,
    FailureComponent: LoadingComponent
});

export const ForUsersWithBinderValidationFeatureAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingBinderSelector,
    predicate: binderValidationFeatureAccess,
    FailureComponent: LoadingComponent
});

export const ForUsersAtInstitutionsWithBinderEvents = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingBinderSelector,
    predicate: binderEventFeatureAccess,
    FailureComponent: LoadingComponent
});

export const ForUsersWithStudyFeatureAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: studyFeatureAccess,
    failureRedirectPath: (state) => homeRouteSelector(state)
});

export const ForUsersWithViewStudyExpectationAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: viewStudyExpectationsPredicate,
    failureRedirectPath: (state) => homeRouteSelector(state)
});

export const ForUsersWithManageStudyExpectationsAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: manageStudyExpectationsPredicate,
    failureRedirectPath: state => homeRouteSelector(state)
});

export const ForUsersAtInstitutionsWithDashboard = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: dashboardFeatureAccess,
    failureRedirectPath: (state) => binderRouteSelector(state)
});

export const ForUsersWithStudyEventFeatureAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: (state) => currentStudyEventAccess(state) && viewStudyEventsPredicate(state),
    failureRedirectPath: state => homeRouteSelector(state)
});

export const ForUsersWithManageEventTypes = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: manageEventTypeFeatureAccess,
    failureRedirectPath: (state) => binderRouteSelector(state)
});

export const ForUsersWithViewStudyDashboardAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: viewStudyDashboardPredicate,
    failureRedirectPath: (state) => defaultStudyFallbackRouteSelector(state)
});
export const ForUsersWithManageTasksAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: manageStudyTasksPredicate,
    failureRedirectPath: (state) => homeRouteSelector(state)
});

export const ForUsersWithNewInboxFeatureAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: () => true,
    failureRedirectPath: state => homeRouteSelector(state)
});

export const ForUsersWithV4FeatureAccess = UserAuthWrapper({
    authSelector: state => state,
    authenticatingSelector: loadingMeSelector,
    predicate: () => loadHomePageV4Access,
    failureRedirectPath: state => homeRouteSelector(state)
});
